const REASON_CODES = {
  SEASON: "factor:regional_coastal_seasonality",
  REGIONAL_SEASONAL_AVAILABILITY: "factor:regional_seasonal_availability",
  TEMPERATURE: "factor:water_temperature_activity",
  MISSING_TEMPERATURE: "missing:water_temperature",
  THERMAL_AVAILABILITY: "factor:thermal_habitat_availability",
  MISSING_SST: "missing:sst",
  CARP_STRONG: "common_carp_temperature_strongly_supports_activity",
  CARP_MODERATE: "common_carp_temperature_moderately_supports_activity",
  CARP_COLD: "common_carp_cold_water_suppresses_activity",
  CARP_WARM: "common_carp_very_warm_water_reduces_activity",
  CARP_TEMPERATURE_MISSING: "trusted_freshwater_temperature_unavailable",
  CARP_TEMPERATURE_UNSUPPORTED:
    "trusted_freshwater_temperature_above_supported_range",
  POPULATION_UNKNOWN: "population_evidence_unknown",
  POPULATION_ABSENT: "population_verified_absent",
};

const reasonFor = (reasons, ...codes) =>
  reasons.find((item) => codes.includes(item.code));

const componentQuality = (point, id) => {
  const component = point?.confidence?.components?.find((item) => item.id === id);
  return Number.isFinite(component?.quality) ? component.quality : null;
};

const directionalKey = (prefix, direction) =>
  `${prefix}_${["positive", "neutral", "negative"].includes(direction) ? direction : "neutral"}`;

export const buildPointExplanationModel = ({ point, context = {} } = {}) => {
  const reasons = Array.isArray(point?.reasons) ? point.reasons : [];
  const eligibilityReasons = Array.isArray(point?.eligibility?.reasonCodes)
    ? point.eligibility.reasonCodes
    : [];
  const items = [];
  reasons.filter((item) => ["modifier:marine_platform_shore", "modifier:marine_platform_boat_kayak"].includes(item.code))
    .forEach((item) => items.push({ id: item.code, key: item.code.replace("modifier:", "") }));
  const seaBassReasons = reasons.filter((item) => /^sea_bass_(season|thermal|diel)(_missing)?$/.test(item.code));
  seaBassReasons.forEach((item) => items.push({ id: item.code, key: item.code }));
  const isSeaBassMultifactor = context.seaBassMultifactor === true || seaBassReasons.length > 0;
  const season = reasonFor(
    reasons,
    REASON_CODES.SEASON,
    REASON_CODES.REGIONAL_SEASONAL_AVAILABILITY
  );
  if (season) {
    items.push({
      id: "season",
      key: directionalKey("fishing_today_intelligence_explanation_season", season.direction),
    });
  }

  const carpReasonKeys = {
    [REASON_CODES.CARP_STRONG]:
      "fishing_today_intelligence_explanation_carp_temperature_strong",
    [REASON_CODES.CARP_MODERATE]:
      "fishing_today_intelligence_explanation_carp_temperature_moderate",
    [REASON_CODES.CARP_COLD]:
      "fishing_today_intelligence_explanation_carp_temperature_cold",
    [REASON_CODES.CARP_WARM]:
      "fishing_today_intelligence_explanation_carp_temperature_warm",
    [REASON_CODES.CARP_TEMPERATURE_MISSING]:
      "fishing_today_intelligence_explanation_carp_temperature_missing",
    [REASON_CODES.CARP_TEMPERATURE_UNSUPPORTED]:
      "fishing_today_intelligence_explanation_carp_temperature_unsupported",
  };
  const carpReason = reasons.find((item) => carpReasonKeys[item.code]);
  if (carpReason) {
    items.push({ id: "carp_temperature", key: carpReasonKeys[carpReason.code] });
  }
  if (eligibilityReasons.includes(REASON_CODES.POPULATION_UNKNOWN)) {
    items.push({
      id: "population_unknown",
      key: "fishing_today_intelligence_explanation_carp_population_unknown",
    });
  } else if (eligibilityReasons.includes(REASON_CODES.POPULATION_ABSENT)) {
    items.push({
      id: "population_absent",
      key: "fishing_today_intelligence_explanation_carp_population_absent",
    });
  }

  const temperatureMissing =
    reasons.some((item) => item.code === REASON_CODES.MISSING_TEMPERATURE) ||
    point?.missingInputs?.includes("water_temperature");
  const temperature = reasons.find(
    (item) => item.code === REASON_CODES.TEMPERATURE
  );
  if (temperatureMissing && !isSeaBassMultifactor) {
    items.push({
      id: "temperature_missing",
      key: "fishing_today_intelligence_explanation_temperature_missing",
    });
  } else if (temperature) {
    items.push({
      id: "temperature",
      key: directionalKey(
        "fishing_today_intelligence_explanation_temperature",
        temperature.direction
      ),
    });
  }

  const availabilityTemperatureMissing =
    reasons.some((item) => item.code === REASON_CODES.MISSING_SST) ||
    point?.missingInputs?.includes("sst");
  const availabilityTemperature = reasonFor(
    reasons,
    REASON_CODES.THERMAL_AVAILABILITY
  );
  if (availabilityTemperatureMissing) {
    items.push({
      id: "availability_temperature_missing",
      key: "fishing_today_intelligence_explanation_temperature_availability_missing",
    });
  } else if (availabilityTemperature) {
    items.push({
      id: "availability_temperature",
      key: directionalKey(
        "fishing_today_intelligence_explanation_temperature_availability",
        availabilityTemperature.direction
      ),
    });
  }

  const confidenceItems = [];
  const environmentalCompleteness = componentQuality(
    point,
    "environmental_completeness"
  );
  if (environmentalCompleteness !== null && environmentalCompleteness < 100) {
    confidenceItems.push({
      id: "confidence_environment",
      key: "fishing_today_intelligence_explanation_confidence_environment",
    });
  }
  const spatialPrecision = componentQuality(point, "spatial_precision");
  if (
    context.locationPrecision === "profile_city" &&
    spatialPrecision !== null &&
    spatialPrecision < 100
  ) {
    confidenceItems.push({
      id: "confidence_profile_city",
      key: "fishing_today_intelligence_explanation_confidence_profile_city",
    });
  }
  const horizonQuality = componentQuality(point, "forecast_horizon_quality");
  if (horizonQuality !== null && horizonQuality < 100) {
    confidenceItems.push({
      id: "confidence_horizon",
      key: "fishing_today_intelligence_explanation_confidence_horizon",
    });
  }
  const calibrationMaturity = componentQuality(point, "calibration_maturity");
  if (calibrationMaturity !== null && calibrationMaturity < 100) {
    confidenceItems.push({
      id: "confidence_calibration",
      key: "fishing_today_intelligence_explanation_confidence_calibration",
    });
  }

  return {
    items: [...items, ...confidenceItems.slice(0, 2)],
    hasBiologicalExplanation: items.length > 0,
    hasTemperatureFactor: Boolean(
      seaBassReasons.some((item) => item.code.startsWith("sea_bass_thermal")) ||
      carpReason ||
      temperatureMissing ||
        temperature ||
        availabilityTemperatureMissing ||
        availabilityTemperature
    ),
    hasSeasonFactor: Boolean(season) || seaBassReasons.some((item) => item.code === "sea_bass_season"),
    hasDielFactor: seaBassReasons.some((item) => item.code === "sea_bass_diel"),
    isSeaBassMultifactor,
    isCommonCarp: Boolean(
      carpReason ||
        eligibilityReasons.includes(REASON_CODES.POPULATION_UNKNOWN) ||
        eligibilityReasons.includes(REASON_CODES.POPULATION_ABSENT)
    ),
  };
};

export const fishingIntelligenceExplanationCodes = REASON_CODES;
